import React, { useEffect, useState } from "react";
import { CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { Card, PrimaryButton } from "./UI";
import api from "../lib/api";
import { ChoiceSelect } from "./ChoiceSelect";

const HOURS = Array.from({ length: 33 }, (_, i) => {
  const h = 6 + Math.floor(i / 2), m = i % 2 ? "30" : "00";
  const v = `${String(h).padStart(2, "0")}:${m}`;
  return { value: v, label: `${h % 12 || 12}:${m} ${h < 12 ? "AM" : "PM"}` };
});

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const ZONES = ["America/Los_Angeles", "America/Denver", "America/Phoenix", "America/Chicago", "America/New_York", "America/Anchorage", "Pacific/Honolulu"];

export const AvailabilitySettings = () => {
  const [av, setAv] = useState(null);
  const [reminders, setReminders] = useState(null);
  useEffect(() => {
    api.get("/settings/availability").then(({ data }) => setAv(data));
    api.get("/reminders/config").then(({ data }) => setReminders(data));
  }, []);
  if (!av) return null;
  const toggleDay = (d) => setAv({ ...av, days: av.days.includes(d) ? av.days.filter((x) => x !== d) : [...av.days, d].sort() });
  const save = async () => {
    try { await api.put("/settings/availability", { ...av, session_minutes: Number(av.session_minutes) }); toast.success("Online booking hours saved"); }
    catch { toast.error("Check your hours — start must be before end"); }
  };
  return (
    <Card delay={0.12} testid="settings-availability">
      <h3 className="font-serif text-xl mb-1 flex items-center gap-2"><CalendarClock className="w-5 h-5" style={{ color: "var(--mf-accent)" }} /> Online booking hours</h3>
      <p className="text-xs mb-4" style={{ color: "var(--mf-text-muted)" }}>Clients can book open hourly slots in the portal (12h minimum notice).</p>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {DAYS.map((d, i) => (
          <button key={d} onClick={() => toggleDay(i)} className="w-11 py-1.5 rounded-full text-xs font-medium" style={{ background: av.days.includes(i) ? "var(--mf-primary)" : "var(--mf-muted)", color: av.days.includes(i) ? "#FAFAF8" : "var(--mf-text)" }} data-testid={`avail-day-${i}`}>{d}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <ChoiceSelect value={av.start} onChange={(v) => setAv({ ...av, start: v })} options={HOURS} testid="avail-start" />
        <ChoiceSelect value={av.end} onChange={(v) => setAv({ ...av, end: v })} options={HOURS} testid="avail-end" />
        <ChoiceSelect value={av.session_minutes} onChange={(v) => setAv({ ...av, session_minutes: Number(v) })} testid="avail-length"
          options={[30, 45, 50, 60, 90].map((m) => ({ value: m, label: `${m} min sessions` }))} />
        <ChoiceSelect value={av.timezone} onChange={(v) => setAv({ ...av, timezone: v })} testid="avail-timezone"
          options={ZONES.map((z) => ({ value: z, label: z.split("/")[1].replace("_", " ") }))} />
      </div>
      <PrimaryButton onClick={save} testid="avail-save">Save hours</PrimaryButton>
      {reminders && (
        <div className="text-xs mt-4" style={{ color: "var(--mf-text-muted)" }} data-testid="reminder-channels">
          Client reminders: Email <b style={{ color: "#4a7a4e" }}>on</b> · Text {reminders.sms_enabled ? <b style={{ color: "#4a7a4e" }}>on</b> : <b>waiting for Twilio setup</b>}
        </div>
      )}
    </Card>
  );
};
